import { fn } from '@app/utils';
import { apiEndpoints, defaultErrorMessage } from '@app/utils/constants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api, { getApiErrors } from '@app/services/api';
import type { AxiosError } from 'axios';
import type {
    ApiError,
    LoginParams,
    LoginResponse,
    LogoutResponse,
    RefreshSessionParams,
    RefreshSessionResponse,
    ResendMfaParams,
    ResendMfaResponse,
    PasswordResetParams,
    PasswordResetResponse,
    SetPasswordParams,
} from '@app/services/api.types';
import useApp from '../useApp';

export const authLogin = async (params: LoginParams): Promise<LoginResponse> => {
    const url = apiEndpoints.auth.login;
    const response = await api.post(url, params);
    return response?.data?.data;
};

export const authLogout = async (): Promise<LogoutResponse> => {
    const url = apiEndpoints.auth.logout;
    const response = await api.post(url);
    return response?.data;
};

export const refreshToken = async (
    params?: RefreshSessionParams
): Promise<RefreshSessionResponse> => {
    const url = apiEndpoints.auth.refreshToken;
    const response = await api.post(url, undefined, {
        headers: {
            'refresh-token': params?.refreshToken,
        },
    });
    return response?.data?.data;
};

export const resendMfa = async (params: ResendMfaParams): Promise<ResendMfaResponse> => {
    const url = apiEndpoints.auth.resendMfa;
    const response = await api.post(url, params);
    return response?.data;
};

export const passwordReset = async (
    params: PasswordResetParams
): Promise<PasswordResetResponse> => {
    const url = apiEndpoints.auth.startPasswordReset;
    const response = await api.post(url, params);
    return response?.data;
};

export const setNewPassword = async (params: SetPasswordParams): Promise<PasswordResetResponse> => {
    const url = apiEndpoints.auth.setNewPassword;
    const response = await api.post(url, params);
    return response?.data;
};

export const useAuthActions = () => {
    const { message } = useApp();
    const queryClient = useQueryClient();

    const login = useMutation<LoginResponse, AxiosError<ApiError>, LoginParams>({
        mutationFn: async (params) => authLogin(params),
        onSuccess(data) {
            const expiresIn = 3600; // Defaults to 60 mins

            if (data?.token) {
                fn.storeAccessToken(data?.token, {
                    lifespan: expiresIn,
                });
                fn.storeRefreshToken(data?.refresh_token, {
                    lifespan: expiresIn,
                });
                // trigger refetch on me endpoint
                queryClient.refetchQueries({ queryKey: ['me'] });
            }
        },
        onError: (error) => {
            fn.clearAllData();

            const response = error.response?.data;
            let errorMessage;

            switch (error.response?.status) {
                case 401: // Unauthorized
                    errorMessage = 'Incorrect email or password, please try again.';
                    break;
                case 403: // Forbidden
                    errorMessage = 'You do not have permission to perform this action.';
                    break;
                case 404: // Not Found
                    errorMessage = 'The requested resource was not found.';
                    break;
                case 500: // Internal Server Error
                    errorMessage = 'An unexpected error occurred. Please try again later.';
                    break;
                default:
                    errorMessage = getApiErrors(response?.message, true);
                    break;
            }

            message.error({
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [['me']],
            skipGlobalErrorHandling: true,
        },
    });

    const logout = useMutation<LogoutResponse, AxiosError<ApiError>, void>({
        mutationFn: async () => authLogout(),
        onSettled: () => {
            fn.clearAllData();
        },
        meta: {
            skipGlobalErrorHandling: true,
        },
    });

    const resendMfaCode = useMutation<ResendMfaResponse, AxiosError<ApiError>, ResendMfaParams>({
        mutationFn: async (params) => resendMfa(params),
        onSuccess() {
            message.success({
                content: 'MFA Code sent!',
            });
        },
        onError: (error) => {
            // clear the tokens
            fn.clearAllData();

            // show the error message
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.message, true);
            message.error({
                content: errorMessage || defaultErrorMessage,
            });
        },
    });

    const startPasswordReset = useMutation<
        PasswordResetResponse,
        AxiosError<ApiError>,
        PasswordResetParams
    >({
        mutationFn: async (params) => passwordReset(params),
        onSuccess() {
            message.success({
                content: 'A link to reset your password has been sent to your email.',
            });
        },
        onError: (error) => {
            // clear the tokens
            fn.clearAllData();

            // show the error message
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.message, true);
            message.error({
                content: errorMessage || defaultErrorMessage,
            });
        },
    });

    const setPassword = useMutation<PasswordResetResponse, AxiosError<ApiError>, SetPasswordParams>(
        {
            mutationFn: async (params) => setNewPassword(params),
            onSuccess() {
                message.success({
                    content:
                        'Your password has been successfully reset. You can now login with your new password.',
                });
            },
            onError: (error) => {
                // clear the tokens
                fn.clearAllData();

                // show the error message
                const response = error.response?.data;
                const errorMessage = getApiErrors(response?.message, true);
                message.error({
                    content: errorMessage || defaultErrorMessage,
                });
            },
        }
    );

    return {
        login,
        logout,
        resendMfaCode,
        startPasswordReset,
        setPassword,
    };
};

export default useAuthActions;
